import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, SelectField } from "@atlas/ui/web";
import { atlas } from "../../../lib/sdk";
import {
  PRODUTO_LABEL,
  STATUS_LABEL,
  contratoToProposta,
  fmtBRL,
  getBancoPerfil,
  setBancoPerfil,
  BANCO_PERFIS,
  travaInfo,
  type BancoProduto,
  type BancoPropostaFromApi as PropostaRow,
  type BancoPropostaStatus,
} from "../../../lib/banco-propostas";

// SO os status que o backend realmente emite. `contratoToProposta`
// (lib/banco-propostas.ts:371) traduz a `situacao` da API em apenas 4:
//   "aguard"                        -> recebida (tambem e' o fallback)
//   "aprov"                         -> aprovada
//   "ativo" | "averb" | "quitad"    -> averbada
//   "cancel" | "suspens" | "recus"  -> recusada
// Os outros 5 (em_analise, aguardando_formalizacao, formalizada, mais_info,
// expirada) existem no type mas nenhuma origem os produz — o unico caminho
// seria o overlay de localStorage, e `patchProposta` nao e' chamada em lugar
// nenhum. Deixa-los no dropdown so gerava filtro que nunca acha nada
// (cliente reportou 23/07/2026 selecionando "Formalizada" e vendo vazio).
// Ordem = fluxo real da proposta.
const STATUS_OPTS: BancoPropostaStatus[] = [
  "recebida", "aprovada", "averbada", "recusada",
];

type TabKey = "todas" | "aguardando" | "aprovadas" | "recusadas";
// Produto virou submenu do sidebar (padrao Cadastros) — nao aparece mais como
// tab/dropdown na pagina. Vem do query param ?produto=emprestimo|cartao|portabilidade.

/** Rotulo do tipo de operacao a partir do produto + sinais de portabilidade.
 *  Cliente reportou 28/07/2026: submenu Cartao mostrava "Novo emprestimo" nos
 *  cards porque a heuristica antiga so distinguia portabilidade vs "resto". */
function tipoLabelDoProduto(p: PropostaRow): string {
  if (p.produto === "portabilidade") {
    return p.saldoDevedorOrigem != null && p.valor > p.saldoDevedorOrigem
      ? "Portabilidade com troco"
      : "Portabilidade simples";
  }
  if (p.produto === "cartao") return "Cartão consignado";
  if (p.produto === "cartao_beneficio") return "Cartão benefício";
  return "Novo empréstimo";
}

function statusPertenceTab(s: BancoPropostaStatus, tab: TabKey): boolean {
  if (tab === "todas") return true;
  if (tab === "aguardando") return s === "recebida" || s === "em_analise" || s === "mais_info";
  // Averbada saiu de "Aprovadas" (cliente 23/07/2026): averbacao confirmada e'
  // operacao FECHADA, nao proposta aprovada esperando desfecho. NAO ganha aba
  // propria (cliente: "ja tem um seletor, nao precisa criar uma aba pra cada")
  // — quem quiser so as averbadas usa o dropdown de Status. Elas seguem
  // visiveis em "Todas".
  if (tab === "aprovadas") return s === "aprovada" || s === "aguardando_formalizacao" || s === "formalizada";
  return s === "recusada" || s === "expirada";
}

export function BancoPropostas() {
  const nav = useNavigate();
  // Produto agora vem do QUERY PARAM (?produto=emprestimo|cartao|portabilidade) —
  // controlado pelo submenu do sidebar do banco (mesmo padrao de Cadastros).
  // Se nao vier, cai no default "emprestimo".
  const [sp] = useSearchParams();
  const produtoTab: BancoProduto = (() => {
    const p = sp.get("produto");
    if (p === "cartao" || p === "portabilidade" || p === "emprestimo" || p === "cartao_beneficio") return p;
    return "emprestimo";
  })();
  const [tab, setTab] = useState<TabKey>("todas");
  const [status, setStatus] = useState<"" | BancoPropostaStatus>("");
  const [expirando, setExpirando] = useState(false);
  const [perfilId, setPerfilId] = useState(() => getBancoPerfil().id);

  // Re-render a cada segundo para os countdowns.
  const [, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1_000);
    return () => clearInterval(t);
  }, []);

  const apiQ = useQuery({
    queryKey: ["banco", "propostas-api"],
    queryFn: () => atlas.banco.contratos(),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });

  const todas: PropostaRow[] = useMemo(
    () => (apiQ.data?.contratos ?? []).map(contratoToProposta),
    [apiQ.data],
  );

  // Recorte por produto ativo (aba primaria). Todos os contadores/tabs de status
  // e a listagem operam SOMENTE dentro deste recorte.
  const doProduto = useMemo(
    () => todas.filter((p) => p.produto === produtoTab),
    [todas, produtoTab],
  );

  // Contagem por produto pra badge das abas primarias.
  const totaisPorProduto = useMemo(() => {
    const m: Record<BancoProduto, number> = { emprestimo: 0, cartao: 0, cartao_beneficio: 0, portabilidade: 0 };
    for (const p of todas) m[p.produto]++;
    return m;
  }, [todas]);

  // Contadores por status dentro do produto ativo — header + tabs de status.
  // "aprovadas este mes" usa aprovadoEm (data da decisao) em vez de criadaEm
  // (data da proposta) — cliente reportou 28/07/2026 que estava agrupando pelo
  // mes errado quando proposta criada em X era aprovada em Y.
  const contadores = useMemo(() => {
    let aguardando = 0, aprovadas = 0, averbadas = 0, recusadas = 0, aprovadasNoMes = 0;
    const agora = new Date();
    const mesAtual = agora.getMonth();
    const anoAtual = agora.getFullYear();
    for (const p of doProduto) {
      if (statusPertenceTab(p.status, "aguardando")) aguardando++;
      else if (statusPertenceTab(p.status, "aprovadas")) {
        aprovadas++;
        // Fallback pra criadaEm quando aprovadoEm ainda nao foi computado
        // (contratos legados sem evento de aprovar registrado).
        const ref = p.aprovadoEm ?? p.criadaEm;
        const d = new Date(ref);
        if (d.getMonth() === mesAtual && d.getFullYear() === anoAtual) aprovadasNoMes++;
      } else if (p.status === "averbada") averbadas++;
      else if (statusPertenceTab(p.status, "recusadas")) recusadas++;
    }
    return { total: doProduto.length, aguardando, aprovadas, averbadas, aprovadasNoMes, recusadas };
  }, [doProduto]);

  const filtradas = useMemo(() => {
    const isPendente = (s: BancoPropostaStatus) => statusPertenceTab(s, "aguardando");
    const atividade = (p: PropostaRow) => new Date(p.criadaEm).getTime() || 0;
    return doProduto
      .filter((p) => {
        if (!statusPertenceTab(p.status, tab)) return false;
        if (status && p.status !== status) return false;
        if (expirando) {
          const t = travaInfo(p);
          if (!t || t.expirada || !t.urgente) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const pa = isPendente(a.status) ? 0 : 1;
        const pb = isPendente(b.status) ? 0 : 1;
        if (pa !== pb) return pa - pb;
        return atividade(b) - atividade(a);
      });
  }, [doProduto, tab, status, expirando]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Marketplace
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem", letterSpacing: "-0.02em" }}>Minhas Propostas</h1>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 6, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <span><b style={{ color: "var(--gold-500)" }}>{contadores.aguardando}</b> aguardando análise</span>
            <span style={{ color: "var(--border-strong)" }}>·</span>
            <span><b style={{ color: "var(--emerald-500)" }}>{contadores.aprovadasNoMes}</b> aprovadas este mês</span>
            <span style={{ color: "var(--border-strong)" }}>·</span>
            {/* Averbadas ganhou linha propria no header junto com a aba: sem
                isso o contador "aprovadas este mes" caia pra 0 (elas saem do
                grupo) e a operacao fechada sumia da leitura de topo. */}
            <span><b style={{ color: "var(--accent)" }}>{contadores.averbadas}</b> averbadas</span>
            <span style={{ color: "var(--border-strong)" }}>·</span>
            <span><b style={{ color: "var(--danger-500)" }}>{contadores.recusadas}</b> recusadas</span>
          </div>
        </div>
        <PerfilSwitcher value={perfilId} onChange={(id) => { setBancoPerfil(id); setPerfilId(id); }} />
      </header>

      {/* Produto agora e' escolhido no submenu do sidebar (padrao Cadastros).
          Os contadores por produto seguem disponiveis via totaisPorProduto pra
          futuras extensoes; por ora, so mostramos o nome do produto ativo. */}
      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
        Produto ativo: <b style={{ color: "var(--text)" }}>{PRODUTO_LABEL[produtoTab]}</b> ({totaisPorProduto[produtoTab]})
      </div>

      {/* Tabs de status (dentro do produto ativo) */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <Tab active={tab === "todas"} onClick={() => setTab("todas")} label={`Todas (${contadores.total})`} tone="neutro" />
        <Tab active={tab === "aguardando"} onClick={() => setTab("aguardando")} label={`Aguardando (${contadores.aguardando})`} tone="gold" />
        <Tab active={tab === "aprovadas"} onClick={() => setTab("aprovadas")} label={`Aprovadas (${contadores.aprovadas})`} tone="emerald" />
        <Tab active={tab === "recusadas"} onClick={() => setTab("recusadas")} label={`Recusadas (${contadores.recusadas})`} tone="danger" />
      </div>

      {/* Filtros restantes — status (fino) + trava expirando.
          Convenio saiu (ja e' o ativo no topo) e Produto virou aba primaria. */}
      <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
        <SelectField
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as "" | BancoPropostaStatus)}
          options={[{ value: "", label: "Todos" }, ...STATUS_OPTS.map((s) => ({ value: s, label: STATUS_LABEL[s] }))]}
        />
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--text-muted)", paddingBottom: 8 }}>
          <input type="checkbox" checked={expirando} onChange={(e) => setExpirando(e.target.checked)} />
          Só trava expirando (24h)
        </label>
      </div>

      {/* Cards de propostas */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {apiQ.isError && filtradas.length === 0 ? (
          // Erro precede vazio: sem esse ramo, uma falha de fetch mostrava
          // "Nenhuma proposta" como se a fila estivesse vazia. Polling de 5s
          // se recupera sozinho. Convencao do projeto (30/07/2026).
          <div style={{ padding: 40, textAlign: "center", color: "var(--danger-500)", fontSize: 14, border: "1px dashed var(--danger-500)", borderRadius: 12 }}>
            Não foi possível carregar as propostas. Tentando novamente…
          </div>
        ) : filtradas.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)", fontSize: 14, border: "1px dashed var(--border)", borderRadius: 12 }}>
            Nenhuma proposta de <b>{PRODUTO_LABEL[produtoTab]}</b> neste filtro.
          </div>
        ) : (
          filtradas.map((p) => (
            <PropostaCard
              key={p.idUnico}
              proposta={p}
              onAbrir={() => nav(`/banco/propostas/${p.idUnico}`)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function PropostaCard({
  proposta: p,
  onAbrir,
}: {
  proposta: PropostaRow;
  onAbrir: () => void;
}) {
  const trava = travaInfo(p);
  const isPortabilidade = p.produto === "portabilidade";
  const temTroco = isPortabilidade && p.saldoDevedorOrigem != null && p.valor > p.saldoDevedorOrigem;
  const trocoValor = temTroco ? p.valor - (p.saldoDevedorOrigem ?? 0) : 0;
  const isPendente = p.status === "recebida" || p.status === "em_analise" || p.status === "mais_info";

  // Card grande destacado: portabilidade com troco, em analise, com trava.
  if (temTroco && isPendente) {
    return <FeaturedPortabilidadeCard proposta={p} trocoValor={trocoValor} trava={trava} onAbrir={onAbrir} />;
  }
  // Card padrao: aguardando analise.
  if (isPendente) {
    return <PendenteCard proposta={p} isPortabilidade={isPortabilidade} onAbrir={onAbrir} />;
  }
  // Card compacto: aprovadas/recusadas/averbadas.
  return <DecididaCard proposta={p} isPortabilidade={isPortabilidade} onAbrir={onAbrir} />;
}

/** Card destacado — portabilidade com troco, ainda em análise. */
function FeaturedPortabilidadeCard({
  proposta: p,
  trocoValor,
  trava,
  onAbrir,
}: {
  proposta: PropostaRow;
  trocoValor: number;
  trava: ReturnType<typeof travaInfo>;
  onAbrir: () => void;
}) {
  const diasRestantes = trava && !trava.expirada ? Math.max(0, Math.floor(trava.msRestantes / 86_400_000)) : 0;
  return (
    <article style={{
      background: "color-mix(in srgb, var(--gold-500) 6%, var(--surface))",
      border: "1px solid var(--gold-500)",
      borderRadius: 14,
      padding: 20,
      position: "relative",
    }}>
      {/* faixa do topo */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--gold-600, var(--gold-500))" }}>
          <span>Margem pré-reservada</span>
          <span style={{ color: "var(--border-strong)" }}>·</span>
          <span>Portabilidade com troco</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={pillStyle("gold")}>INTENCIONADO</span>
          {trava && !trava.expirada ? (
            <span style={{ ...pillStyle("gold"), background: "transparent" }}>
              {diasRestantes > 0 ? `${diasRestantes} dia${diasRestantes > 1 ? "s" : ""} restantes` : "vence hoje"}
            </span>
          ) : null}
        </div>
      </div>

      {/* servidor */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: "1.15rem", fontWeight: 700, color: "var(--text)" }}>{p.nome}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
            Mat: <span style={{ fontFamily: "var(--font-mono)" }}>{p.matricula}</span>
            {" · "}CPF: <span style={{ fontFamily: "var(--font-mono)" }}>{p.cpfMasked}</span>
            {" · "}{p.convenio}
          </div>
        </div>
      </div>

      {/* 3 sub-cards: CONTRATO ATUAL / PROPOSTA / TROCO */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 14 }}>
        <SubCard
          label="CONTRATO ATUAL"
          banco={p.bancoOrigem ?? "Banco de origem"}
          detalhe={`${p.saldoDevedorOrigem != null ? "Saldo: " + fmtBRL(p.saldoDevedorOrigem) : "—"}`}
          tone="neutro"
        />
        <SubCard
          label="PROPOSTA"
          banco="Você"
          detalhe={`${p.taxaAm.toFixed(2)}% a.m. · ${fmtBRL(p.parcela)}/mês`}
          tone="info"
        />
        <SubCard
          label="TROCO LIBERADO"
          banco={fmtBRL(trocoValor)}
          detalhe="crédito adicional"
          tone="gold"
        />
      </div>

      <div style={{ padding: "10px 14px", borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", border: "1px dashed var(--gold-500)", fontSize: 12.5, color: "var(--text-muted)", marginBottom: 14 }}>
        Esta margem está <b style={{ color: "var(--text)" }}>bloqueada exclusivamente para você</b> {diasRestantes > 1 ? `pelos próximos ${diasRestantes} dias` : diasRestantes === 1 ? "por menos de 1 dia" : "por poucas horas (vence hoje)"}. Outras instituições não podem averbar durante este período.
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button onClick={onAbrir}>Ver mais →</Button>
      </div>
    </article>
  );
}

/** Card padrão pra propostas aguardando análise. */
function PendenteCard({
  proposta: p,
  isPortabilidade,
  onAbrir,
}: {
  proposta: PropostaRow;
  isPortabilidade: boolean;
  onAbrir: () => void;
}) {
  const tipoLabel = tipoLabelDoProduto(p);
  const ehCartao = p.produto === "cartao" || p.produto === "cartao_beneficio";

  return (
    <article style={{
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: 14,
      padding: 18,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={pillStyle("gold")}>AGUARDANDO ANÁLISE</span>
        <span style={{ fontSize: 13, color: "var(--text-muted)" }}>{tipoLabel}</span>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: "1rem", fontWeight: 700, color: "var(--text)" }}>
            {p.nome} <span style={{ fontWeight: 400, color: "var(--text-dim)" }}>· Mat: <span style={{ fontFamily: "var(--font-mono)" }}>{p.matricula}</span></span>
          </div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
            {isPortabilidade && p.bancoOrigem ? (
              <>
                <b>{p.bancoOrigem}</b>
                {p.saldoDevedorOrigem != null ? <span> ({fmtBRL(p.saldoDevedorOrigem)} de saldo)</span> : null}
                {" → "}
                <b>Você</b> ({p.taxaAm.toFixed(2)}% a.m. · {fmtBRL(p.parcela)}/mês)
              </>
            ) : ehCartao ? (
              // Cartao: nao ha "parcelas/valor liberado" — a operacao e' um limite
              // com fatura minima descontada em folha.
              <>
                {p.taxaAm.toFixed(2)}% a.m. · Fatura mínima {fmtBRL(p.parcela)}/mês · Limite {fmtBRL(p.valor)}
              </>
            ) : (
              <>
                {p.taxaAm.toFixed(2)}% a.m. · {fmtBRL(p.parcela)}/mês · {p.parcelas}x · {fmtBRL(p.valor)} liberado
              </>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <Button size="sm" onClick={onAbrir}>Ver mais →</Button>
        </div>
      </div>
    </article>
  );
}

/** Card compacto pra propostas já decididas. */
function DecididaCard({
  proposta: p,
  isPortabilidade,
  onAbrir,
}: {
  proposta: PropostaRow;
  isPortabilidade: boolean;
  onAbrir: () => void;
}) {
  const isAprovada = p.status === "aprovada" || p.status === "aguardando_formalizacao" || p.status === "formalizada" || p.status === "averbada";
  const tone = isAprovada ? "emerald" : "danger";
  const rotulo = isAprovada
    ? p.status === "averbada" ? "AVERBADA" : "APROVADA"
    : p.status === "expirada" ? "EXPIRADA" : "RECUSADA";
  const tipoLabel = tipoLabelDoProduto(p);

  // Idade da DECISAO, nao da proposta — usa o timestamp do evento
  // correspondente vindo do backend. Fallback pra criadaEm mantem cards
  // legados (sem evento) exibindo algo em vez de "há NaN dias".
  const ref = p.status === "averbada" ? (p.averbadoEm ?? p.aprovadoEm ?? p.criadaEm)
    : isAprovada ? (p.aprovadoEm ?? p.criadaEm)
    : (p.recusadoEm ?? p.criadaEm);
  const diasDesde = Math.max(0, Math.floor((Date.now() - new Date(ref).getTime()) / 86_400_000));
  const rotuloAcao = p.status === "averbada" ? "Averbada" : isAprovada ? "Aprovada" : p.status === "expirada" ? "Expirou" : "Recusada";
  const detalheStatus = `${rotuloAcao} há ${diasDesde} dia${diasDesde === 1 ? "" : "s"}${p.status === "averbada" ? " · Averbação confirmada" : ""}`;

  return (
    <article
      onClick={onAbrir}
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: "14px 18px",
        cursor: "pointer",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={pillStyle(tone)}>{rotulo}</span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>
            {p.nome} — {tipoLabel}
            {isPortabilidade && p.bancoOrigem ? <span style={{ color: "var(--text-muted)", fontWeight: 400 }}> · {p.bancoOrigem}</span> : null}
          </div>
          <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>{detalheStatus}</div>
        </div>
        <span style={{ color: "var(--text-dim)", fontSize: 18 }}>›</span>
      </div>
    </article>
  );
}

function SubCard({ label, banco, detalhe, tone }: { label: string; banco: string; detalhe: string; tone: "neutro" | "info" | "gold" }) {
  const bgColor = tone === "gold" ? "color-mix(in srgb, var(--gold-500) 10%, transparent)"
    : tone === "info" ? "color-mix(in srgb, var(--accent) 8%, transparent)"
    : "var(--bg-elev)";
  const borderColor = tone === "gold" ? "var(--gold-500)"
    : tone === "info" ? "color-mix(in srgb, var(--accent) 40%, var(--border))"
    : "var(--border)";
  return (
    <div style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 10, padding: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", marginTop: 4 }}>{banco}</div>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{detalhe}</div>
    </div>
  );
}

// ProdutoTab removida 27/07/2026 — codigo morto (produto virou submenu no
// sidebar, componente nao era mais chamado no JSX). Auditoria UX tier 4.

function Tab({ active, onClick, label, tone }: { active: boolean; onClick: () => void; label: string; tone: "neutro" | "gold" | "emerald" | "danger" }) {
  const activeColor =
    tone === "gold" ? "var(--gold-500)"
    : tone === "emerald" ? "var(--emerald-500)"
    : tone === "danger" ? "var(--danger-500)"
    : "var(--accent)";
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "8px 14px",
        borderRadius: 999,
        border: `1px solid ${active ? activeColor : "var(--border)"}`,
        background: active ? `color-mix(in srgb, ${activeColor} 12%, transparent)` : "transparent",
        color: active ? activeColor : "var(--text-muted)",
        fontSize: 13,
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

function pillStyle(tone: "gold" | "emerald" | "danger" | "neutro"): React.CSSProperties {
  const c = tone === "gold" ? "var(--gold-500)"
    : tone === "emerald" ? "var(--emerald-500)"
    : tone === "danger" ? "var(--danger-500)"
    : "var(--text-muted)";
  return {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: ".06em",
    color: c,
    border: `1px solid ${c}`,
    background: `color-mix(in srgb, ${c} 8%, transparent)`,
    borderRadius: 6,
    padding: "3px 8px",
    textTransform: "uppercase",
  };
}

function PerfilSwitcher({ value, onChange }: { value: string; onChange: (id: string) => void }) {
  return (
    <div style={{ minWidth: 220 }}>
      <SelectField
        label="Perfil do operador"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={BANCO_PERFIS.map((p) => ({ value: p.id, label: `${p.nome} (${p.papel})` }))}
      />
    </div>
  );
}
